<ng-container *transloco="let t; read: 'providers.feedPage.gtfs'">
  @switch (pane()) {
    @case ('realtime') {
      <div class="flex flex-col gap-5">
        <p class="m-0 max-w-[680px] text-[13px] leading-5 text-ink-2">{{ t(realtime() ? 'realtimePane.introOn' : 'realtimePane.introOff') }}</p>

        <app-gtfs-field
          [label]="t('fields.tripUpdatesUrl')"
          [help]="t('realtimePane.tripUpdatesHelp')"
          [error]="errorText('tripUpdatesUrl')"
        >
          <app-text-input
            [ngModel]="value().tripUpdatesUrl"
            (ngModelChange)="patch({ tripUpdatesUrl: $event })"
            [ngModelOptions]="{ standalone: true }"
            [placeholder]="t('fields.urlPlaceholder')"
          ></app-text-input>
        </app-gtfs-field>

        <div class="grid grid-cols-1 gap-x-5 gap-y-4 md:grid-cols-2">
          <app-gtfs-field [label]="t('realtimePane.alertsUrl')" [error]="errorText('alertsUrl')">
            <app-text-input
              [ngModel]="value().alertsUrl"
              (ngModelChange)="patch({ alertsUrl: $event })"
              [ngModelOptions]="{ standalone: true }"
              [disabled]="!realtime()"
              [placeholder]="t('fields.urlPlaceholder')"
            ></app-text-input>
          </app-gtfs-field>
          <app-gtfs-field [label]="t('realtimePane.vehiclePositionsUrl')" [error]="errorText('vehiclePositionsUrl')">
            <app-text-input
              [ngModel]="value().vehiclePositionsUrl"
              (ngModelChange)="patch({ vehiclePositionsUrl: $event })"
              [ngModelOptions]="{ standalone: true }"
              [disabled]="!realtime()"
              [placeholder]="t('fields.urlPlaceholder')"
            ></app-text-input>
          </app-gtfs-field>
        </div>

        <div class="flex flex-col gap-1.5 pt-5 shadow-[inset_0_1px_0_var(--color-hairline)]">
          <label class="flex items-center gap-3 text-[13px] font-semibold text-ink-1" [class.opacity-50]="!cleardownEnabled()">
            <app-toggle
              [ngModel]="value().cleardownOnVehiclePosition"
              (ngModelChange)="patch({ cleardownOnVehiclePosition: $event })"
              [ngModelOptions]="{ standalone: true }"
              [disabled]="!cleardownEnabled()"
            ></app-toggle>
            {{ t('realtimePane.cleardown') }}
          </label>
          <span class="text-[12px] leading-4 text-ink-3">{{ t('realtimePane.cleardownHelp') }}</span>
        </div>
      </div>
    }

    @case ('departures') {
      <div class="flex flex-col gap-5">
        <div class="grid grid-cols-1 gap-x-5 gap-y-4 md:grid-cols-2">
          <app-gtfs-field
            [label]="t('departuresPane.boardWindow')"
            [help]="t('departuresPane.boardWindowHelp', { default: defaults.boardWindowSecs })"
            [error]="errorText('boardWindowSecs', minimums.boardWindowSecs)"
          >
            <app-number-input
              [ngModel]="value().boardWindowSecs"
              (ngModelChange)="patch({ boardWindowSecs: $event })"
              [ngModelOptions]="{ standalone: true }"
              [min]="minimums.boardWindowSecs"
              [step]="1"
              [placeholder]="'' + defaults.boardWindowSecs"
            ></app-number-input>
          </app-gtfs-field>
          <app-gtfs-field
            [label]="t('departuresPane.lookback')"
            [help]="t('departuresPane.lookbackHelp', { default: defaults.boardLookbackSecs })"
            [error]="errorText('boardLookbackSecs', minimums.boardLookbackSecs)"
          >
            <app-number-input
              [ngModel]="value().boardLookbackSecs"
              (ngModelChange)="patch({ boardLookbackSecs: $event })"
              [ngModelOptions]="{ standalone: true }"
              [min]="minimums.boardLookbackSecs"
              [step]="1"
              [placeholder]="'' + defaults.boardLookbackSecs"
            ></app-number-input>
          </app-gtfs-field>
        </div>

        <div class="grid grid-cols-1 gap-x-5 md:grid-cols-2">
          @let unrecognised = value().unrecognisedLineNameSource;
          <app-gtfs-field
            [label]="t('fields.lineNameSource')"
            [help]="unrecognised && !value().lineNameSource ? t('departuresPane.lineNameUnrecognised', { value: unrecognised }) : t('departuresPane.lineNameHelp')"
            [danger]="!!unrecognised && !value().lineNameSource"
          >
            <app-select
              [options]="lineNameOptions()"
              [ngModel]="value().lineNameSource"
              (ngModelChange)="setLineNameSource($event)"
              [ngModelOptions]="{ standalone: true }"
              [placeholder]="unrecognised ? t('departuresPane.lineNamePlaceholderUnrecognised', { value: unrecognised }) : ''"
              [invalid]="!!unrecognised && !value().lineNameSource"
            ></app-select>
          </app-gtfs-field>
        </div>
      </div>
    }

    @case ('advanced') {
      <div class="flex flex-col gap-5">
        <p class="m-0 max-w-[680px] text-[13px] leading-5 text-ink-2">{{ t('advancedPane.intro') }}</p>
        <div class="grid grid-cols-1 gap-x-5 gap-y-4 md:grid-cols-3">
          <app-gtfs-field
            [label]="t('advancedPane.pollInterval')"
            [help]="t('advancedPane.defaultValue', { default: defaults.pollIntervalSecs })"
            [error]="errorText('pollIntervalSecs', minimums.pollIntervalSecs)"
          >
            <app-number-input
              [ngModel]="value().pollIntervalSecs"
              (ngModelChange)="patch({ pollIntervalSecs: $event })"
              [ngModelOptions]="{ standalone: true }"
              [min]="minimums.pollIntervalSecs"
              [step]="1"
              [unit]="t('secondsUnit')"
              [placeholder]="'' + defaults.pollIntervalSecs"
            ></app-number-input>
          </app-gtfs-field>
          <app-gtfs-field
            [label]="t('advancedPane.changeThreshold')"
            [help]="t('advancedPane.defaultValue', { default: defaults.significantChangeThresholdSecs })"
            [error]="errorText('significantChangeThresholdSecs', minimums.significantChangeThresholdSecs)"
          >
            <app-number-input
              [ngModel]="value().significantChangeThresholdSecs"
              (ngModelChange)="patch({ significantChangeThresholdSecs: $event })"
              [ngModelOptions]="{ standalone: true }"
              [min]="minimums.significantChangeThresholdSecs"
              [step]="1"
              [unit]="t('secondsUnit')"
              [placeholder]="'' + defaults.significantChangeThresholdSecs"
            ></app-number-input>
          </app-gtfs-field>
          <app-gtfs-field
            [label]="t('advancedPane.heartbeat')"
            [help]="t('advancedPane.defaultValue', { default: defaults.heartbeatIntervalSecs })"
            [error]="errorText('heartbeatIntervalSecs', minimums.heartbeatIntervalSecs)"
          >
            <app-number-input
              [ngModel]="value().heartbeatIntervalSecs"
              (ngModelChange)="patch({ heartbeatIntervalSecs: $event })"
              [ngModelOptions]="{ standalone: true }"
              [min]="minimums.heartbeatIntervalSecs"
              [step]="1"
              [unit]="t('secondsUnit')"
              [placeholder]="'' + defaults.heartbeatIntervalSecs"
            ></app-number-input>
          </app-gtfs-field>
        </div>
      </div>
    }
  }
</ng-container>
